import type { FormGroup } from '@angular/forms';
import { DropdownData, Input, Value } from './input.interface';
import type { ReactiveChangeOrigin } from '../utils/change-origin/reactive-change-origin';
import type { ReactiveFieldDependency } from '../utils/dependencies/reactive-dependency-graph';
import type { ReactiveFormSerializerDefinition } from './reactive-form-serializer.interface';

export type FormButtonType = 'callback' | 'routerLink' | 'externalLink';


export interface ReactiveDropdownDataSourceContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup | null;
    readonly previousData: T | null;
    readonly changedField: string | null;
    readonly changeOrigin: ReactiveChangeOrigin;
    readonly selectedRawValues: Readonly<Record<string, unknown | null>>;
    readonly getSelectedRaw: <TEntity = unknown>(fieldName: string) => TEntity | null;
    readonly getSelectedLabel: (fieldName: string) => string;
    readonly getSelectedBackendValue: (fieldName: string) => unknown | null;
}

export type ReactiveDropdownDataSourceParams<T = unknown> =
    Record<string, unknown> |
    ((context: ReactiveDropdownDataSourceContext<T>) => Record<string, unknown>);

export type ReactiveDropdownResponseMapper<T = unknown> = (
    response: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => DropdownData<unknown> | Value<unknown>[] | unknown[];

export type ReactiveDropdownOptionMapper<T = unknown> = (
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => Value<unknown>;

export type ReactiveDropdownOptionFilter<T = unknown> = (
    option: Value<unknown>,
    context: ReactiveDropdownDataSourceContext<T>,
) => boolean;

export type ReactiveDropdownFallbackValueMapper<T = unknown> = (
    item: unknown,
    context: ReactiveDropdownDataSourceContext<T>,
) => unknown;

export type ReactiveDropdownKeySelector<T = unknown> =
    string |
    string[] |
    ((item: unknown, context: ReactiveDropdownDataSourceContext<T>) => unknown);

export interface ReactiveDropdownRetryConfiguration {
    attempts?: number;
    delayMs?: number;
}

export interface ReactiveDropdownDataSource<T = unknown> {
    endpoint: unknown;
    cacheKey: string;
    lazy?: boolean;
    dependsOn?: readonly string[];
    params?: ReactiveDropdownDataSourceParams<T>;
    ttlMs?: number;
    timeoutMs?: number;
    retry?: ReactiveDropdownRetryConfiguration;
    forceReload?: boolean;
    cacheEmptyResults?: boolean;
    responsePath?: string;
    labelKey?: string;
    labelKeys?: readonly string[];
    valueKey?: string;
    valueKeys?: readonly string[];
    fallbackValue?: ReactiveDropdownFallbackValueMapper<T>;
    distinctBy?: ReactiveDropdownKeySelector<T>;
    mapResponse?: ReactiveDropdownResponseMapper<T>;
    mapOption?: ReactiveDropdownOptionMapper<T>;
    filterOption?: ReactiveDropdownOptionFilter<T>;
}

export type ReactiveDropdownCatalogPhase = 'critical' | 'deferred' | 'lazy';

export interface ReactiveDropdownCatalogs {
    /** Dropdown field names that must be loaded before the form is considered ready. */
    readonly critical?: readonly string[];
    /** Dropdown field names that can be loaded after the first stable render. */
    readonly deferred?: readonly string[];
    /** Dropdown field names that should only be loaded on explicit user demand. */
    readonly lazy?: readonly string[];
}

export type ReactiveFormSupportDataPhase = ReactiveDropdownCatalogPhase;

export type ReactiveFormSupportDataCacheStorage = 'memory' | 'persistent';

export type ReactiveFormSupportDataCacheStatus = 'miss' | 'fresh' | 'stale';

export type ReactiveFormSupportDataMetricsTrigger = 'initial' | 'background';

export type ReactiveFormSupportDataSourceKind = 'custom' | 'declarative';

export interface ReactiveFormSupportDataDiagnosticsThreshold {
    readonly maxAverageMs?: number;
    readonly maxMs?: number;
    readonly maxAverageNetworkMs?: number;
    readonly maxCacheMs?: number;
}

export interface ReactiveFormSupportDataMetricsEvent {
    readonly formName: string | null;
    readonly sourceKey: string;
    readonly sourceKind: ReactiveFormSupportDataSourceKind;
    readonly phase: ReactiveFormSupportDataPhase | 'all';
    readonly trigger: ReactiveFormSupportDataMetricsTrigger;
    readonly cacheStatus: ReactiveFormSupportDataCacheStatus;
    readonly cacheHit: boolean;
    readonly staleHit: boolean;
    readonly networkMs: number | null;
    readonly phaseMs: number;
    readonly ok: boolean;
    readonly diagnosticsThreshold?: ReactiveFormSupportDataDiagnosticsThreshold;
    readonly error?: unknown;
}

export type ReactiveFormSupportDataMetricsSink = (event: ReactiveFormSupportDataMetricsEvent) => void;

export interface ReactiveFormSupportDataContext<T = unknown> {
    readonly model: FormGroup | null;
    readonly previousData: T | null;
    readonly getValue: (fieldName: string) => unknown;
}

export interface ReactiveFormSupportDataEndpoint<TResponse = unknown> {
    unsuscribe(): unknown;
    response(callback: (value: TResponse) => void): unknown;
    safePerform(params?: unknown): unknown;
    fetchStatus?(callback: (status: 'syncing' | 'finish' | 'error') => void): unknown;
    readonly isLoading?: boolean | undefined;
}

export type ReactiveFormSupportDataParams<T = unknown> =
    Record<string, unknown> |
    ((context: ReactiveFormSupportDataContext<T>) => Record<string, unknown>);

export type ReactiveFormSupportDataLabelSelector<TItem = unknown> =
    string |
    readonly string[] |
    ((item: TItem) => unknown) |
    { readonly or: readonly string[] };

export type ReactiveFormSupportDataTarget<T = unknown> =
    DropdownData<unknown> |
    readonly DropdownData<unknown>[] |
    ((context: ReactiveFormSupportDataContext<T>) => DropdownData<unknown> | readonly DropdownData<unknown>[]);

export type ReactiveFormSupportDataResult = unknown;

export type ReactiveFormSupportDataResponseMapper<T = unknown> = (
    response: unknown,
    context: ReactiveFormSupportDataContext<T>,
) => ReactiveFormSupportDataResult;

export type ReactiveFormSupportDataSuccessHandler<T = unknown> = (
    result: ReactiveFormSupportDataResult,
    context: ReactiveFormSupportDataContext<T>,
) => Promise<void> | void;

export type ReactiveFormSupportDataErrorHandler<T = unknown> = (
    error: unknown,
    context: ReactiveFormSupportDataContext<T>,
) => Promise<void> | void;

export interface ReactiveFormSupportDataSource<T = unknown> {
    readonly key: string;
    readonly phase?: ReactiveFormSupportDataPhase;
    readonly cacheKey?: string;
    readonly ttlMs?: number;
    readonly staleTtlMs?: number;
    readonly cacheStorage?: ReactiveFormSupportDataCacheStorage;
    readonly staleWhileRevalidate?: boolean;
    readonly dependsOn?: readonly string[];
    readonly endpoint?: ReactiveFormSupportDataEndpoint;
    readonly params?: ReactiveFormSupportDataParams<T>;
    readonly target?: ReactiveFormSupportDataTarget<T>;
    readonly label?: ReactiveFormSupportDataLabelSelector;
    readonly labelKey?: string;
    readonly labelKeys?: readonly string[];
    readonly noPlaceholder?: boolean;
    readonly preserveExistingValues?: boolean;
    readonly timeoutMs?: number;
    readonly diagnosticsThreshold?: ReactiveFormSupportDataDiagnosticsThreshold;
    readonly mapResponse?: ReactiveFormSupportDataResponseMapper<T>;
    readonly onSuccess?: ReactiveFormSupportDataSuccessHandler<T>;
    readonly onError?: ReactiveFormSupportDataErrorHandler<T>;
    readonly load?: (context: ReactiveFormSupportDataContext<T>) => Promise<unknown> | unknown;
}

export interface ReactiveDropdownDependencyBehaviour {
    clearOnParentChange?: boolean;
    /** Clears the dependent control for these origins before falling back to the global origin policy. */
    clearOnOrigins?: readonly ReactiveChangeOrigin[];
    /** Preserves the dependent control for these origins even when the global policy would clear it. */
    preserveOnOrigins?: readonly ReactiveChangeOrigin[];
    /** Keeps the child value while dependencies are being hydrated from existing data. */
    clearOnHydration?: boolean;
    /** Keeps a saved value when the loaded option catalog no longer contains it. */
    preserveValueWhenOptionsMissing?: boolean;
    /** Shows a field warning when the saved value cannot be resolved against the loaded catalog. */
    warnWhenSelectedValueMissing?: boolean;
    autoSelect?: 'singleOption' | false;
    keepValueUntilOptionsLoaded?: boolean;
    /** Keeps selected multi-select values until the new child catalog has been loaded and intersected. */
    keepCurrentValueIfStillAvailable?: boolean;
    /** Reopens the PrimeNG select or multi-select panel after dependent options are reloaded. */
    preservePanelOnReload?: boolean;
    /** Restores the current multi-select filter after dependent options are reloaded. */
    preserveFilterOnReload?: boolean;
    /** Shows a temporary disabled loading option instead of an empty list while a dependent dropdown reloads. */
    showLoadingOptionDuringReload?: boolean;
    reloadOnParentChange?: boolean;
    filterLocally?: boolean;
}

export interface FormButton {
    type?: FormButtonType;
    label: string;
    icon?: string;
    class?: string;
    style?: string | Record<string, string>;
    isVisible?: boolean;
    function?: () => void;
    route?: Array<string | number>;
    queryParams?: Record<string, unknown>;
    fragment?: string;
    href?: string;
    target?: string;
    rel?: string;
    tooltip?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    tooltipEvent?: 'hover' | 'focus';
}

export type FormSignatureUploadStrategy = 'taskFile' | 'deferredBase64';

export interface FormSignatureExistingImageConfiguration {
    idField?: string;
    pathPrefix: string;
    extension?: string;
}

export interface FormSignatureConfiguration {
    database?: string;
    folder: string;
    field?: string;
    uploadStrategy?: FormSignatureUploadStrategy;
    existingImageConfiguration?: FormSignatureExistingImageConfiguration;
}


export interface FormUploadButton {
    isEnabled: boolean;
    label: string;
    imageName?: string;
    imageFolder?: string;
    imageDisplay?: 'inline' | 'link' | 'header';
    accept?: string;
    allowedExtensions?: string[];
    invalidFormatMessage?: string;
    uploadUrl?: string;
    viewFolder?: string;
    showPreview?: boolean;
    maxFileSizeBytes?: number;
    idField?: string;
}

export interface ReactiveBeforeRepeatableHydrationContext<T> {
    readonly previousData: T;
    readonly form: Form<T>;
}

export interface Form<T> {
    name: string,
    header: string,
    type: FormType,
    sections: FormSection<T>[],
    table: FormTable | null,
    uploadButton: FormUploadButton,
    uploadButtons?: FormUploadButton[],
    /**
     * Header buttons rendered next to the upload button.
     * `button` is kept for backward compatibility with legacy forms.
     */
    button?: FormButton | FormButton[],
    buttons?: FormButton[],
    signed: boolean,
    signatureConfiguration?: FormSignatureConfiguration,
    /** Declarative backend payload serializer. */
    serializer?: ReactiveFormSerializerDefinition,
    /** Normalizes data loaded for edit/duplicate mode before patching the Angular model. */
    previousDataTransform?: (previousData: T) => T,
    /** Loads per-row dropdown dependencies after scalar controls are patched and before repeatables are hydrated. */
    beforeRepeatableHydration?: (context: ReactiveBeforeRepeatableHydrationContext<T>) => void | Promise<void>,
    /** Explicit dependency graph. Legacy field-level dependencies are adapted automatically. */
    dependencies?: readonly ReactiveFieldDependency[]
    /** Declarative dropdown loading phases used by the builder to avoid manual build()/pool() gates. */
    dropdownCatalogs?: ReactiveDropdownCatalogs
    /** Declarative support data loaded by the form load orchestrator. */
    supportDataSources?: readonly ReactiveFormSupportDataSource<T>[]
}

export interface ReactiveSectionLogicContext<T = unknown> {
    readonly model: FormGroup;
    /** Typed snapshot of the current form value for section-level logic. */
    readonly value: Readonly<Partial<T>>;
    readonly getValue: (fieldName: string) => unknown;
    readonly getRawValue: () => Record<string, unknown>;
}

export type ReactiveSectionVisibilityRule<T = unknown> =
    boolean |
    ((context: ReactiveSectionLogicContext<T>) => boolean);

export interface FormSection<T> {
    name: string,
    fields: FormField<T>[],
    buttons?: FormButton[],
    tables?: FormTable[],
    width: number,
    isVisible: boolean,
    /** Declarative section visibility evaluated by the builder against the hydrated runtime model. */
    visibleWhen?: ReactiveSectionVisibilityRule<T>,
    order: number
}

export interface ReactiveFieldLogicContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup;
    readonly changedField: string | null;
    readonly changeOrigin: ReactiveChangeOrigin;
    readonly currentValue: unknown;
    readonly getValue: (fieldName: string) => unknown;
    readonly getRawValue: () => Record<string, unknown>;
}

export interface ReactiveComputedFieldContext<T = unknown> {
    readonly field: FormField<T>;
    readonly model: FormGroup;
    readonly currentValue: unknown;
    readonly getValue: (fieldName: string) => unknown;
    readonly getRawValue: () => Record<string, unknown>;
}

export interface ReactiveComputedField<T = unknown> {
    readonly dependsOn?: readonly string[];
    readonly emitEvent?: boolean;
    /** Skip the computed resolver while any declared dependency is empty. Required validators still own empty-state errors. */
    readonly skipWhenDependenciesEmpty?: boolean;
    readonly resolve: (context: ReactiveComputedFieldContext<T>) => unknown;
}

export interface ReactiveFieldCondition<T = unknown> {
    readonly dependsOn?: readonly string[];
    readonly resolve: (context: ReactiveFieldLogicContext<T>) => boolean;
}

export type ReactiveFieldConditionRule<T = unknown> =
    boolean |
    ((context: ReactiveFieldLogicContext<T>) => boolean) |
    ReactiveFieldCondition<T>;

export interface ReactiveFieldLabelRule<T = unknown> {
    readonly dependsOn?: readonly string[];
    readonly resolve: (context: ReactiveFieldLogicContext<T>) => string;
}

export interface FormField<T> {
    [key: string]:
        ((it: FormField<T>) => T | string | null | undefined) |
        ((it: FormField<T>) => void) |
        Input<T> |
        FormFieldConditions |
        string[] |
        string |
        number |
        boolean |
        FormButton[] |
        any |
        undefined;
    name: string;
    label: string;
    input: Input<T>;
    dataSource?: ReactiveDropdownDataSource<T>;
    dependencyBehaviour?: ReactiveDropdownDependencyBehaviour;
    /** Declarative visibility condition. Prefer this over new `turnVisible` callbacks. */
    visibleWhen?: ReactiveFieldConditionRule<T>;
    /** Declarative read-only condition. It keeps the control enabled but renders the field as non-editable. */
    readonlyWhen?: ReactiveFieldConditionRule<T>;
    /** Declarative disabled condition. It renders the field as non-editable and disables the Angular control. */
    disabledWhen?: ReactiveFieldConditionRule<T>;
    /** Declarative calculated value. Prefer this over new `updateValue` usages. */
    computed?: ReactiveComputedField<T>;
    /** Declarative calculated label. Prefer this over label mutations inside legacy callbacks. */
    labelWhen?: ReactiveFieldLabelRule<T>;
    /** Explicit dependencies owned by this field. Use this instead of explicit dependency metadata. */
    dependencies?: readonly ReactiveFieldDependency[];
    /** Optional helper text rendered below the input. Use it for domain recommendations or constraints. */
    helpText?: string;
    checkForDuplicates?: (it: string) => boolean;
    width: number,
    conditions: FormFieldConditions;
    buttons?: FormButton[],
    order: number;
    turnVisible?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => void;
    updateValue?: (it: FormField<T>, context?: ReactiveFieldLogicContext<T>) => T | string | null | undefined;
}

export interface FormTable {
    title: string,
    showIndex: boolean,
    data: string[] | string[][] | undefined,
    style: string[] | string[][] | undefined
}

export interface FormFieldConditions {
    isRememberedOnDuplicate: boolean;
    isRequired: boolean;
    isRetained: boolean;
    hasUndoEnabled: boolean;
    isVisible: boolean;
}

export enum FormType {
    CREATION,
    EDITION,
    DUPLICATION,
    DELETION,
    INFORMATIVE,
    EXPORT,
    GENERATION
}
